// What to show a merchant when a promise rejects with something that is not
// an `Error`.
//
// Key export: describeFailure.
// Neighbours: groups/discovery/pages/MenuEditorPage.tsx and
// groups/takeout/pages/TakeoutOrdersPage.tsx (its callers), services/takeout.ts
// (the layer whose rejections reach them).
//
// **A bare "Could not read the board." is the worst of the two answers.** The
// session and signing layers can reject with a plain object or a string, and a
// `err instanceof Error ? err.message : '<fallback>'` reports nothing at all in
// exactly the case where the merchant most needs to be told what happened —
// twice now, a rejection carrying a real message was replaced by a sentence
// that named only the screen it happened on.

/** The most specific message the rejected value carries. */
export function describeFailure(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message
  if (typeof err === 'string' && err) return err
  if (err && typeof err === 'object') {
    const shape = err as { message?: unknown; error?: unknown; reason?: unknown }
    for (const field of [shape.message, shape.error, shape.reason]) {
      if (typeof field === 'string' && field) return field
    }
    try {
      return `${fallback} ${JSON.stringify(err)}`
    } catch {
      /* circular or otherwise unserializable — fall through */
    }
  }
  return `${fallback} It carried no message; check the browser console.`
}
